/**
 * The study's welcome as rich text — a port of the consoles'
 * `webui/static/shared/richtext.js`. The server renders the study's limited
 * Markdown to HTML from a fixed set of tags (domain/welcome_markdown.py); this
 * is the second, independent check. The HTML is parsed into an inert document
 * (DOMParser runs no scripts and loads nothing) and only the allowed elements
 * are rebuilt into the widget. Anything else contributes its text only.
 */

const ALLOWED = new Set(["P", "BR", "STRONG", "EM", "H3", "UL", "OL", "LI", "A"]);
// Dropped with everything inside them: their text is code, not copy.
const DROPPED = new Set([
  "SCRIPT", "STYLE", "TEMPLATE", "NOSCRIPT", "IFRAME", "OBJECT", "EMBED",
  "TEXTAREA", "TITLE", "SVG", "MATH", "HEAD",
]);

function safeHref(href: string | null): string | null {
  const h = (href || "").trim();
  const lower = h.toLowerCase();
  return (lower.startsWith("https://") && h.length > 8) ||
    (lower.startsWith("mailto:") && h.length > 7) ? h : null;
}

function rebuild(from: Node, into: Node): void {
  for (const node of Array.from(from.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      into.appendChild(document.createTextNode(node.nodeValue ?? ""));
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      // SVG and MathML report lower-case tag names
      const tag = el.tagName.toUpperCase();
      if (DROPPED.has(tag)) continue;
      if (!ALLOWED.has(tag)) {
        rebuild(el, into);   // keep its text, drop the element
        continue;
      }
      if (tag === "A") {
        const href = safeHref(el.getAttribute("href"));
        if (!href) { rebuild(el, into); continue; }
        const a = document.createElement("a");
        a.href = href;
        a.rel = "noopener noreferrer nofollow";
        a.target = "_blank";
        rebuild(el, a);
        into.appendChild(a);
        continue;
      }
      const out = document.createElement(tag.toLowerCase());
      rebuild(el, out);
      into.appendChild(out);
    }
    // comments, processing instructions: dropped
  }
}

/** Replace `el`'s content with the allowlisted rebuild of `html`. */
export function setRichText(el: HTMLElement, html: string): void {
  const doc = new DOMParser().parseFromString(`<body>${html || ""}</body>`, "text/html");
  const out = document.createDocumentFragment();
  rebuild(doc.body, out);
  el.replaceChildren(out);
}
